import type { ReceptorGateway, QueuePayload } from './types';
import type { MediaAsset } from '../domain/types';
import { MediaCache } from '../../net/mediaCache';
import { isCacheUsable } from '../player/queue';

/** Cache is scoped to one screen by the caller's IndexedDB name. */
export class CachedGateway implements ReceptorGateway {
  private latest: QueuePayload | null = null;
  private revoked = false;
  private preparing = false;
  private persistedVersion: string | undefined;
  lastContactAt = 0;
  private applying: Promise<void> = Promise.resolve();
  constructor(private readonly remote: ReceptorGateway, private readonly cache: MediaCache,
    private readonly screenId: string, private readonly onRevoked: () => void) {}
  now() { return this.remote.now(); }
  private guard(error: unknown): never {
    const code = (error as { code?: string })?.code;
    if (code === 'DEVICE_TOKEN_REVOKED' || code === 'DEVICE_TOKEN_INVALID') {
      this.revoked = true;
      void this.applying.catch(() => {}).then(() => this.cache.clear()).catch(() => {});
      this.onRevoked();
    }
    throw error;
  }
  async fetchQueue(version?: string): Promise<QueuePayload | null> {
    try { return await this.remote.fetchQueue(version); } catch (error) { return this.guard(error); }
  }
  async heartbeat(state: Parameters<ReceptorGateway['heartbeat']>[0]) {
    try { return await this.remote.heartbeat(state); } catch (error) { return this.guard(error); }
  }
  subscribeQueueUpdates(onChange: (version: string) => void) { return this.remote.subscribeQueueUpdates(onChange); }
  async fetchMediaBytes(media: MediaAsset): Promise<Blob> {
    if (this.revoked) throw new Error('Revoked');
    const cached = await this.cache.readMedia(media.id).catch(() => null);
    if (this.revoked) throw new Error('Revoked');
    if (cached) { return cached.blob; }
    try {
      const blob = await this.remote.fetchMediaBytes(media);
      if (this.revoked) throw new Error('Revoked');
      const result = await this.cache.storeMedia(media, blob);
      if (this.revoked) { await this.cache.clear(); throw new Error('Revoked'); }
      if (!result.ok) throw new Error('Media unavailable within cache quota');
      return blob;
    } catch (error) { return this.guard(error); }
  }
  async restore(): Promise<QueuePayload | null> {
    try {
      const cached = await this.cache.readQueue(this.screenId);
      if (this.revoked || !cached) return null;
      const payload = JSON.parse(cached.rawPayload) as QueuePayload;
      if (payload.screenId !== this.screenId || !isCacheUsable(payload)) return null;
      await this.cache.writeQueue(cached); // restore protection before downloads
      if (this.revoked) { await this.cache.clear(); return null; }
      this.latest = payload;
      this.persistedVersion = payload.version;
      this.lastContactAt = cached.cachedAt;
      return payload;
    } catch { return null; }
  }
  async prepare(payload: QueuePayload): Promise<void> {
    if (payload.screenId !== this.screenId) throw new Error('Wrong screen queue');
    this.preparing = true;
    await this.applying.catch(() => {});
    try {
      for (const item of payload.items) {
        try { await this.fetchMediaBytes(item.media); } catch (error) { if (this.revoked) throw error; }
      }
      this.latest = payload;
    } finally { this.preparing = false; }
  }
  async applied(version: string | undefined): Promise<void> {
    const q = this.latest;
    if (this.preparing || this.revoked || !q || q.version !== version || this.persistedVersion === version) return;
    this.applying = this.applying.catch(() => {}).then(async () => {
    if (this.revoked || this.preparing || this.latest !== q) return;
    await this.cache.writeQueue({ ...q, cachedAt: Date.now(), rawPayload: JSON.stringify(q),
      items: q.items.map(i => ({ adId: i.adId, mediaId: i.media.id, kind: i.media.kind,
        url: i.media.url, durationSeconds: i.durationSeconds })) });
    if (this.revoked) { await this.cache.clear(); return; }
    this.persistedVersion = q.version;
    if (!this.revoked && !this.preparing && this.latest === q) await this.cache.evict(q.items.map(i => i.media.id));
    });
    return this.applying;
  }
}
